import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { AuthProvider } from './auth/AuthProvider';
import { Layout } from './components/Layout';
import { RequireAuth } from './components/RequireAuth';
import { I18nProvider } from './i18n';
import { AppleStudioPage } from './pages/AppleStudio';
import { DashboardPage } from './pages/Dashboard';
import { GoogleStudioPage } from './pages/GoogleStudio';
import { LoginPage } from './pages/Login';
import { PassDetailPage } from './pages/PassDetail';
import { TermsPage } from './pages/Terms';
import { VerifyPage } from './pages/Verify';

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 10_000 } },
});

export function App(): JSX.Element {
  return (
    <I18nProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/auth/verify" element={<VerifyPage />} />
              {/* Public — the sign-in page links here, so no RequireAuth. */}
              <Route path="/terms" element={<TermsPage />} />
              <Route element={<Layout />}>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route
                  path="/dashboard"
                  element={
                    <RequireAuth>
                      <DashboardPage />
                    </RequireAuth>
                  }
                />
                {/* The legacy mint forms and the branding page are gone (the
                  studios replaced them) — old bookmarks land on the dashboard. */}
                <Route path="/passes/new" element={<Navigate to="/dashboard" replace />} />
                <Route
                  path="/passes/:id"
                  element={
                    <RequireAuth>
                      <PassDetailPage />
                    </RequireAuth>
                  }
                />
                <Route path="/branding" element={<Navigate to="/dashboard" replace />} />
                {/* Studio URL shape (TEMPLATES-ONLY): /studio/<platform>/:template
                  goes STRAIGHT to the editor — the template fixes the style, no
                  style segment. The registered :template/:style route only
                  absorbs legacy deep links: the page redirects them to
                  /studio/<platform>/:template (style segment ignored). The
                  single-segment route also absorbs the older
                  /studio/<platform>/<style> links — the page maps
                  style → template (generic → altalanos, other known styles →
                  utazas, anything else → template picker). */}
                <Route
                  path="/studio/apple"
                  element={
                    <RequireAuth>
                      <AppleStudioPage />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/studio/apple/:template"
                  element={
                    <RequireAuth>
                      <AppleStudioPage />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/studio/apple/:template/:style"
                  element={
                    <RequireAuth>
                      <AppleStudioPage />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/studio/google"
                  element={
                    <RequireAuth>
                      <GoogleStudioPage />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/studio/google/:template"
                  element={
                    <RequireAuth>
                      <GoogleStudioPage />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/studio/google/:template/:style"
                  element={
                    <RequireAuth>
                      <GoogleStudioPage />
                    </RequireAuth>
                  }
                />
                {/* Legacy deep links → Apple studio (the old PassStudio panel family is deleted — UX4 item 6). */}
                <Route path="/studio" element={<Navigate to="/studio/apple" replace />} />
                <Route
                  path="/studio/:kind/:style"
                  element={<Navigate to="/studio/apple" replace />}
                />
                <Route path="/designer" element={<Navigate to="/studio" replace />} />
              </Route>
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </I18nProvider>
  );
}
